import * as React from 'react';
import { memo, useCallback, useMemo } from 'react';
import cn from 'classnames';
import Autocomplete from '../../../../Autocomplete';
import { TravellerFieldEnum } from '@websky/graphql';
import { ThemeProvider, useTheme } from '../../../../theme';
import { getTravellerFullName, getUserValue } from '../../../../utils';
import { CdnIcon } from '@shared/ui';
import { useTranslation } from 'react-i18next';
import { isWithinInterval } from 'date-fns';
import CompanionItem from './CompanionItem/CompanionItem';
import CompanionGroup from './CompanionGroup/CompanionGroup';
const LastName = memo(props => {
    const { label, field, fieldState, onChange, className, error, onBlur, disabled, variant, autofocus, onSwap, onFillFromDocument } = props;
    const { LastName: theme } = useTheme('Passenger');
    const { Input: inputCss } = useTheme('Input');
    const { t } = useTranslation('Passenger');
    const isLastName = field.type === TravellerFieldEnum.LastName;
    const options = useMemo(() => {
        var _a, _b, _c;
        const profiles = (_a = field.travellerProfiles) !== null && _a !== void 0 ? _a : [];
        if (!profiles.length) {
            return [];
        }
        const search = (_c = (_b = fieldState.input.value) === null || _b === void 0 ? void 0 : _b.toLowerCase()) !== null && _c !== void 0 ? _c : '';
        return profiles.filter(traveller => {
            const filterByBirthDate = isWithinInterval(new Date(getUserValue(traveller, TravellerFieldEnum.BirthDate)), {
                start: field.maxBirthDates.min,
                end: field.maxBirthDates.max
            });
            const isFilledLastNameAndFirstName = traveller.values.some(value => (value.type === TravellerFieldEnum.LastName || value.type === TravellerFieldEnum.FirstName) &&
                !value.value);
            const matchesSearch = !search ||
                traveller.documents.some(doc => {
                    var _a, _b;
                    return ((_a = getUserValue(doc, TravellerFieldEnum.LastName)) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(search)) ||
                        ((_b = getUserValue(doc, TravellerFieldEnum.FirstName)) === null || _b === void 0 ? void 0 : _b.toLowerCase().includes(search));
                });
            return filterByBirthDate && !isFilledLastNameAndFirstName && matchesSearch;
        });
    }, [field.travellerProfiles, field.maxBirthDates, fieldState.input.value]);
    const travellersById = useMemo(() => {
        return options.reduce((acc, traveller) => {
            acc[traveller.id] = traveller;
            return acc;
        }, {});
    }, [options]);
    const onChangeHandler = useCallback((event) => {
        const value = event.target.value;
        onChange === null || onChange === void 0 ? void 0 : onChange(field.name, value);
        fieldState.input.onChange(value);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur();
    }, [onChange, fieldState]);
    const autocompleteTheme = useMemo(() => ({
        Autocomplete: {
            Autocomplete: {
                disabled: inputCss.disabled,
                inner_input: inputCss.input
            }
        }
    }), []);
    const optionGroups = useMemo(() => {
        const groups = [];
        for (const option of options) {
            const traveller = travellersById[option.id];
            const documents = traveller.documents.map(document => ({
                label: option.id,
                value: { traveller, document }
            }));
            if (documents.length === 0) {
                continue;
            }
            groups.push({
                label: (React.createElement(React.Fragment, null,
                    React.createElement("span", null, getTravellerFullName(traveller)),
                    "\u00A0",
                    React.createElement("span", { className: cn(theme.documentCount, {
                            [theme.documentCount_single]: documents.length === 1
                        }) }, t('documentsCount', { count: documents.length })))),
                options: documents
            });
        }
        return groups;
    }, [options, travellersById, t, fieldState.input.value]);
    return (React.createElement(ThemeProvider, { value: autocompleteTheme },
        React.createElement("div", { className: cn(theme.field, {
                [theme.field_swapRight]: onSwap && isLastName,
                [theme.field_swapLeft]: onSwap && !isLastName
            }) },
            React.createElement(Autocomplete, { dropDownClassName: theme.dropdown, name: field.name, label: label, variant: variant, autofocus: autofocus, inputValue: fieldState.input.value, onSelect: onFillFromDocument, onInputChange: onChangeHandler, className: className, error: error, optionRenderer: (option, _, onClick) => {
                    return (React.createElement(CompanionItem, { nationalities: field.nationalities, document: option.value.document, companion: option.value.traveller, onClick: onClick }));
                }, groupRenderer: (group, _, optionRenderer) => {
                    var _a;
                    const groupKey = `${(_a = group.options[0]) === null || _a === void 0 ? void 0 : _a.value.traveller.id}-${group.options
                        .map(option => option.value.document.number + option.value.document.type)
                        .join(',')}`;
                    return React.createElement(CompanionGroup, { key: groupKey, companion: group, optionRender: optionRenderer });
                }, options: optionGroups, disabled: disabled }),
            onSwap && isLastName && (React.createElement("div", { className: theme.swap, onClick: onSwap },
                React.createElement(CdnIcon, { icon: "Action/Sort" }))))));
});
export default LastName;
